import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActors,
  selectSelectedElement,
  selectSelectedTake3D,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import PropAvatar from "../../../components/actors/PropAvatar";
import usePropHelper from "../../../hooks/PropHelper";
import { IConversation } from "../../../models/interactions/Conversation";
import { IEmotionBubble } from "../../../models/interactions/EmotionBubble";
import { IMessageBubble } from "../../../models/interactions/MessageBubble";

export type ConversationLine = IMessageBubble | IEmotionBubble;

/** The selected conversation plus line-level updates. */
export function useConversationLines() {
  const dispatch = useDispatch();
  const conversation = useSelector(selectSelectedElement) as IConversation;

  const save = (messages: ConversationLine[]) =>
    dispatch(updateSelectedInteraction({ ...conversation, messages } as IConversation));

  const updateLine = (id: string, patch: Partial<IMessageBubble & IEmotionBubble>) =>
    save(conversation.messages.map((m) => (m.id === id ? ({ ...m, ...patch } as ConversationLine) : m)));

  const deleteLine = (id: string) => save(conversation.messages.filter((m) => m.id !== id));

  return { conversation, updateLine, deleteLine, save };
}

/**
 * Who can speak in a conversation: every actor, plus the props a bubble can be
 * placed on. Resolves names and avatars for either.
 */
export function useConversationCast(interactionId: string) {
  const actors = useSelector(selectActors);
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps, getPropDTO } = usePropHelper();
  const bubbleProps = take ? getBubbleTargetProps(take, { kind: "timeline", interactionId }) : [];

  const actorOf = (id: string) => actors.find((actor) => actor.id === id);
  const propOf = (id: string) => bubbleProps.find((prop) => prop.id === id);

  const nameOf = (id: string) => actorOf(id)?.workingName ?? propOf(id)?.name ?? "";

  const avatarOf = (id: string) => {
    const prop = propOf(id);
    if (!actorOf(id) && prop) {
      return <PropAvatar size="36" src={getPropDTO(prop.propId)?.propContent?.downloadUri} />;
    }
    return <ActorAvatar actor={actorOf(id)} />;
  };

  return { bubbleProps, nameOf, avatarOf, isProp: (id: string) => Boolean(propOf(id)) };
}
